/**
 * Motor de reservas — orquesta el flujo de CarryOn sobre la máquina de estados
 * (reservations.ts). Lógica pura con el cliente inyectado (CarryonClient), así
 * corre igual con el mock (tests/demo) o el live (functions). Ver
 * docs/architecture/MAQUINA-DE-ESTADOS.md y ADR-0004.
 */
import type { Reservation, ReservationStatus, ReservationOrigin } from './types'
import { assertTransitionReservation } from './reservations'
import type { CarryonClient, SearchCriteria, RoomOption, Holder, Occupant } from './carryon'

let idCounter = 0
function newId(): string {
  idCounter += 1
  return `res-${idCounter}`
}

/** Aplica una transición validada por la máquina de estados. */
function to(res: Reservation, status: ReservationStatus, patch: Partial<Reservation> = {}): Reservation {
  assertTransitionReservation(res.status, status)
  return { ...res, ...patch, status }
}

/** Crea una reserva nueva en estado `buscando`. */
export function createReservation(origin: ReservationOrigin, conversationId?: string): Reservation {
  return {
    id: newId(),
    origin,
    conversationId: conversationId ?? null,
    status: 'buscando',
  }
}

/** Paso 1-2 · Busca opciones y las muestra. */
export async function runSearch(
  client: CarryonClient,
  res: Reservation,
  criteria: SearchCriteria,
): Promise<{ res: Reservation; hotels: Awaited<ReturnType<CarryonClient['search']>>['hotels'] }> {
  const { hotelQuoteId, hotels } = await client.search(criteria)
  return { res: to(res, 'opciones_mostradas', { hotelQuoteId }), hotels }
}

/** El cliente elige una habitación. */
export function selectRoom(res: Reservation, room: RoomOption): Reservation {
  return to(res, 'seleccion', {
    rateKey: room.rateKey,
    nameBroker: room.nameBroker,
    hotelCode: room.hotelCode,
    price: room.price,
    currency: room.currency,
    refundable: room.refundable,
  })
}

/**
 * Paso 3 · Revalida la tarifa. Si el proveedor indica cambio (`update`),
 * pasa a `requote_requerido` (hay que retarificar antes de continuar).
 */
export async function revalidate(
  client: CarryonClient,
  res: Reservation,
  room: RoomOption,
): Promise<Reservation> {
  const r = await client.checkrate(res.hotelQuoteId!, room)
  const changed = r.update.price || r.update.mealPlan || r.update.cancellationPolicy
  if (changed) {
    return to(res, 'requote_requerido', { price: r.price, refundable: r.refundable })
  }
  return to(res, 'tarifa_revalidada', { price: r.price, refundable: r.refundable })
}

/** Paso 4 · Captura pasajeros (prebooking) y deja lista para reservar. */
export async function capturePassengers(
  client: CarryonClient,
  res: Reservation,
  room: RoomOption,
  holder: Holder,
  occupants: Occupant[],
): Promise<Reservation> {
  const r = await client.prebooking(res.hotelQuoteId!, room, holder, occupants)
  const changed = r.update.price || r.update.mealPlan || r.update.cancellationPolicy
  if (changed) {
    return to(res, 'requote_requerido', { price: r.price })
  }
  const withData = to(res, 'datos_pasajeros', { price: r.price })
  return to(withData, 'lista_para_reservar')
}

/**
 * Paso 5 · Reserva. Regla crítica: si el booking falla por red, NO se reintenta
 * a ciegas — se reconcilia el estado real con quoteGet antes de decidir
 * (ver docs/architecture/CARRYON-API.md §idempotencia).
 */
export async function book(
  client: CarryonClient,
  res: Reservation,
  room: RoomOption,
): Promise<Reservation> {
  const reserving = to(res, 'reservando')
  try {
    const b = await client.booking(reserving.hotelQuoteId!, room)
    return to(reserving, 'confirmada', { locator: b.locator })
  } catch {
    // Reconciliación: ¿el proveedor SÍ confirmó pese al error de red?
    const state = await client.quoteGet(reserving.hotelQuoteId!)
    if (state.status === 'CONFIRMED' && state.locator) {
      return to(reserving, 'confirmada', { locator: state.locator })
    }
    return to(reserving, 'error_proveedor')
  }
}

/** Paso 7 · Cancela la reserva. */
export async function cancel(
  client: CarryonClient,
  res: Reservation,
  comment: string,
): Promise<Reservation> {
  await client.cancel(res.hotelQuoteId!, res.locator!, comment)
  return to(res, 'cancelada')
}
